<!--
  StopDetailsEditor: the receiver's details for one stop (pickup or a
  drop-off), edited inline under that stop on the Details step. Was
  StopDetailsDialog; the fields, limits, phone check and cleaning are the
  same, only the modal is gone. Nothing here is sent until the order is
  confirmed, and none of it goes to pricing.

  Edits a local copy, taken when it opens (it is mounted per opening).
  Done hands the cleaned copy back when something changed and closes;
  Remove details clears the stop; Cancel and Esc drop the edits.

  The phone is valid when MazPhoneNumberInput's @update results say so
  (results.isValid); its v-model is then E.164, which is what orders stores.
  No SMS option: SMS is not live, so nothing would be sent.
-->
<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  type StopDetails,
  STOP_LIMITS,
  cleanStopDetails,
  emptyStopDetails,
  hasStopDetails,
} from "../../utils/orderPayload";

const props = defineProps<{
  /** Unique per stop, so the field ids don't clash. */
  idBase: string;
  /** "Pickup · Westlands Mall" or "Drop-off 1 · Sarit Centre", for screen readers. */
  title: string;
  details: StopDetails;
}>();

const emit = defineEmits<{
  (e: "save", value: StopDetails): void;
  (e: "close"): void;
}>();

const draft = reactive<StopDetails>(cleanStopDetails(props.details));
const phoneResult = ref<{ isValid?: boolean } | null>(null);
const phoneTouched = ref(false);
const triedSave = ref(false);
const nameInput = ref<{ focus: () => void } | null>(null);

onMounted(() => nameInput.value?.focus());

const cleaned = computed(() => cleanStopDetails({ ...draft, contact_phone: draft.contact_phone || "" }));
const changed = computed(() => JSON.stringify(cleaned.value) !== JSON.stringify(cleanStopDetails(props.details)));
const hadDetails = computed(() => hasStopDetails(props.details));

const phoneValid = computed(() => !cleaned.value.contact_phone || !!phoneResult.value?.isValid);
const phoneError = computed(() =>
  !phoneValid.value && (phoneTouched.value || triedSave.value)
    ? "Enter a valid phone number, e.g. 0712 345 678"
    : "",
);

const id = (s: string) => `${props.idBase}-${s}`;

function save() {
  triedSave.value = true;
  if (!phoneValid.value) return;
  if (changed.value) emit("save", cleaned.value);
  discarded = true; // saved just now; nothing left to keep on unmount
  emit("close");
}

function removeDetails() {
  discarded = true;
  emit("save", emptyStopDetails());
  emit("close");
}

// Cancel and Esc throw the draft away on purpose. Any other way the editor
// goes (another stop opened, Back to prices, Edit stops) keeps a valid draft:
// unlike the old modal, the rest of the page stays usable while it is open.
let discarded = false;
function cancel() {
  discarded = true;
  emit("close");
}
onBeforeUnmount(() => {
  if (!discarded && changed.value && phoneValid.value) emit("save", cleaned.value);
});

// Esc cancels, unless something inside (the phone field's country list)
// already used it.
function onEsc(e: KeyboardEvent) {
  if (e.defaultPrevented) return;
  const t = e.target as HTMLElement | null;
  if (t?.closest('[role="listbox"], .m-select-list, .m-country-selector__select')) return;
  e.preventDefault();
  e.stopPropagation();
  cancel();
}
</script>

<template>
  <form
    class="sde"
    :aria-label="`Contact for ${title}`"
    @submit.prevent="save"
    @keydown.esc="onEsc"
  >
    <div class="sde__field">
      <label class="ods-label" :for="id('name')">Contact name</label>
      <el-input
        :id="id('name')"
        ref="nameInput"
        v-model="draft.contact_name"
        :maxlength="STOP_LIMITS.contact_name"
        autocomplete="off"
        class="ods-input"
      />
    </div>

    <div class="sde__field" @focusout="phoneTouched = true">
      <span :id="id('phone-label')" class="ods-label">Contact phone</span>
      <div role="group" :aria-labelledby="id('phone-label')">
        <MazPhoneNumberInput
          v-model="draft.contact_phone"
          defaultCountryCode="KE"
          :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
          orientation="row"
          :success="!!draft.contact_phone && phoneResult?.isValid"
          class="sde__phone ac-phone"
          autocomplete="off"
          @update="phoneResult = $event"
        />
      </div>
      <p v-if="phoneError" class="ods-error" role="status">{{ phoneError }}</p>
    </div>

    <div class="sde__field">
      <label class="ods-label" :for="id('ref')">Reference (invoice / delivery note no.)</label>
      <el-input
        :id="id('ref')"
        v-model="draft.reference"
        :maxlength="STOP_LIMITS.reference"
        autocomplete="off"
        class="ods-input"
      />
    </div>

    <div class="sde__field">
      <label class="ods-label" :for="id('instructions')">Instructions for the driver</label>
      <el-input
        :id="id('instructions')"
        v-model="draft.instructions"
        type="textarea"
        :rows="2"
        :autosize="{ minRows: 2, maxRows: 5 }"
        :maxlength="STOP_LIMITS.instructions"
        show-word-limit
        class="ods-input"
      />
    </div>

    <p class="sde__note">
      Only your assigned driver sees this, and only while delivering this order. The driver will call this number on arrival.
    </p>

    <div class="sde__foot">
      <button v-if="hadDetails" type="button" class="sde__remove" @click="removeDetails">Remove details</button>
      <span class="sde__spacer"></span>
      <button type="button" class="sde__btn sde__btn--ghost" @click="cancel">Cancel</button>
      <button type="submit" class="sde__btn sde__btn--primary">Done</button>
    </div>
  </form>
</template>

<style scoped src="./order-form.css"></style>
<style scoped>
.sde {
  font-family: 'DM Sans', sans-serif;
  padding: 12px 0 4px;
}
.sde__field { margin-bottom: 12px; }
.sde__phone { width: 100%; }

.sde__note {
  margin: 0 0 12px;
  color: #64748b;
  font-size: 0.74rem;
  line-height: 1.5;
}

.sde__foot {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sde__spacer { flex: 1; }
.sde__remove {
  min-height: 40px;
  padding: 0 4px;
  border: none;
  background: none;
  color: #b91c1c;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.sde__remove:hover { text-decoration: underline; }
.sde__btn {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}
.sde__btn--ghost { background: transparent; border: none; color: #475569; }
.sde__btn--ghost:hover { color: #12284A; text-decoration: underline; }
.sde__btn--primary {
  border: none;
  background: #12284A;
  color: white;
}
.sde__btn--primary:hover { background: #1e3f73; }
.sde__btn:focus-visible, .sde__remove:focus-visible { outline: 2px solid #2d43ff; outline-offset: 2px; }
</style>
